<script setup lang="ts">
import { computed, ref, h } from 'vue'
import { NDropdown, useMessage } from 'naive-ui'
import type { DropdownOption, TreeOption } from 'naive-ui'
import AppTreeView from '@/components/AppTreeView.vue'
import { pad4 } from '@/control/eventDescriptionUtil'
import TextInputDialog from '@/components/TextInputDialog.vue'
import CustomUIData from '@/model/CustomUI/CustomUIData'
import CustomUIItem from '@/model/CustomUI/CustomUIItem'
import { hydrateIntoInstance } from '@/model/jsonHydrator'
import { useSceneEditorStore } from '@/store/sceneEditor'

type ContextTarget = {
  uiIndex: number
  itemIndex: number | null
}

type ControlClipboard = {
  mode: 'copy' | 'cut'
  item: CustomUIItem
}

type UiClipboard = {
  mode: 'copy' | 'cut'
  item: unknown
}

type UiTreeNodeKind = 'root' | 'load' | 'control' | 'after'

type UiTreeNode = TreeOption & {
  key: string
  label: string
  kind: UiTreeNodeKind
  target: ContextTarget
  children?: UiTreeNode[]
}

const editor = useSceneEditorStore()
const message = useMessage()
const ctxShow = ref(false)
const ctxX = ref(0)
const ctxY = ref(0)
const ctxTarget = ref<ContextTarget | null>(null)
const dragControl = ref<ContextTarget | null>(null)
const uiClipboard = ref<UiClipboard | null>(null)
const controlClipboard = ref<ControlClipboard | null>(null)
const renameModalOpen = ref(false)
const renameDefaultName = ref('')
const renameTarget = ref<ContextTarget | null>(null)
let lastTouchContextAt = 0

function getDefaultName(type: number): string {
  if (type === CustomUIItem.controlType.Button) return '按钮'
  if (type === CustomUIItem.controlType.String) return '文本'
  if (type === CustomUIItem.controlType.Var) return '数值'
  if (type === CustomUIItem.controlType.Pic) return '图片'
  if (type === CustomUIItem.controlType.Drap) return '数值条'
  return '控件'
}

function getItemName(item: CustomUIItem, index: number): string {
  const name = (item.Type === CustomUIItem.controlType.Drap ? item.Color : item.ImagePath2).trim()
  return `${index + 1}. ${name || getDefaultName(item.Type)}`
}

const uiList = computed(() => {
  editor.customUiRevision
  return editor.customUiList
})
const hasControlClipboard = computed(() => Boolean(controlClipboard.value?.item))
const hasUiClipboard = computed(() => Boolean(uiClipboard.value?.item))
function getRootName(ui: { Name?: string }, index: number): string {
  editor.customUiRevision
  return `${pad4(index)}. ${ui.Name?.trim() || 'UI界面'}`
}

function getRootKey(uiIndex: number): string {
  return `ui:${uiIndex}`
}

function getLoadKey(uiIndex: number): string {
  return `ui:${uiIndex}:load`
}

function getAfterKey(uiIndex: number): string {
  return `ui:${uiIndex}:after`
}

function getControlKey(uiIndex: number, itemIndex: number): string {
  return `ui:${uiIndex}:control:${itemIndex}`
}

const uiTreeData = computed<UiTreeNode[]>(() =>
  uiList.value.map((ui, uiIndex) => ({
    key: getRootKey(uiIndex),
    label: getRootName(ui, uiIndex),
    kind: 'root',
    target: { uiIndex, itemIndex: null },
    children: [
      {
        key: getLoadKey(uiIndex),
        label: '初始化',
        kind: 'load',
        target: { uiIndex, itemIndex: null },
      },
      ...ui.ControlList.map((item, itemIndex) => ({
        key: getControlKey(uiIndex, itemIndex),
        label: getItemName(item as CustomUIItem, itemIndex),
        kind: 'control' as const,
        target: { uiIndex, itemIndex },
      })),
      {
        key: getAfterKey(uiIndex),
        label: '初始化完成',
        kind: 'after',
        target: { uiIndex, itemIndex: null },
      },
    ],
  })),
)

const uiExpandedKeys = computed<Array<string | number>>(() =>
  uiList.value.map((_, uiIndex) => getRootKey(uiIndex)),
)

const uiTreeNodeMap = computed(() => {
  const map = new Map<string, UiTreeNode>()
  for (const root of uiTreeData.value) {
    map.set(root.key, root)
    for (const child of root.children ?? []) {
      map.set(child.key, child as UiTreeNode)
    }
  }
  return map
})

const selectedUiKeys = computed<Array<string | number>>(() => {
  const selection = editor.selectedCustomUi
  if (!selection) return []
  if (selection.eventSource === 'load') return [getLoadKey(selection.uiIndex)]
  if (selection.eventSource === 'after') return [getAfterKey(selection.uiIndex)]
  if (selection.itemIndex !== null) return [getControlKey(selection.uiIndex, selection.itemIndex)]
  return [getRootKey(selection.uiIndex)]
})

function addCustomUi(): void {
  editor.createDefaultCustomUi()
}

function addControl(uiIndex: number, type: number): void {
  const ui = editor.customUiList[uiIndex]
  if (!ui || type === CustomUIItem.controlType.Video) return
  const before = editor.cloneCustomUiListSnapshot()
  ui.ControlList.push(new CustomUIItem(type))
  editor.notifyCustomUiChanged()
  editor.selectCustomUiControl(uiIndex, ui.ControlList.length - 1)
  editor.commitCustomUiHistory(before, '新增高级UI控件')
}

function deleteCustomUi(uiIndex: number): void {
  if (editor.customUiList.length <= 1) {
    message.warning('至少保留一个高级 UI')
    return
  }
  if (!window.confirm('确定删除该高级 UI？此操作不可撤销。')) return
  const before = editor.cloneCustomUiListSnapshot()
  editor.customUiList.splice(uiIndex, 1)
  editor.notifyCustomUiChanged()
  const nextIndex = Math.min(uiIndex, editor.customUiList.length - 1)
  if (nextIndex >= 0) editor.selectCustomUiRoot(nextIndex)
  else editor.createDefaultCustomUi(false)
  editor.commitCustomUiHistory(before, '删除高级UI')
}

function cloneCustomUiData(source: unknown): CustomUIData {
  return hydrateIntoInstance(
    new CustomUIData(),
    JSON.parse(JSON.stringify(source)),
    ['customUI', 'CUIList'],
  )
}

function copyCustomUi(uiIndex: number): void {
  const ui = editor.customUiList[uiIndex]
  if (!ui) return
  uiClipboard.value = {
    mode: 'copy',
    item: cloneCustomUiData(ui as CustomUIData),
  }
}

function cutCustomUi(uiIndex: number): void {
  const ui = editor.customUiList[uiIndex]
  if (!ui || editor.customUiList.length <= 1) {
    message.warning('至少保留一个高级 UI')
    return
  }
  const before = editor.cloneCustomUiListSnapshot()
  const [removed] = editor.customUiList.splice(uiIndex, 1)
  if (!removed) return
  uiClipboard.value = {
    mode: 'cut',
    item: removed,
  }
  editor.notifyCustomUiChanged()
  const nextIndex = Math.min(uiIndex, editor.customUiList.length - 1)
  editor.selectCustomUiRoot(nextIndex)
  editor.commitCustomUiHistory(before, '剪切高级UI')
}

function pasteCustomUi(uiIndex: number): void {
  const clipboard = uiClipboard.value
  if (!clipboard) return
  const before = editor.cloneCustomUiListSnapshot()
  const insertAt = Math.min(uiIndex + 1, editor.customUiList.length)
  const item = clipboard.mode === 'cut'
    ? hydrateIntoInstance(new CustomUIData(), clipboard.item, ['customUI', 'CUIList'])
    : cloneCustomUiData(clipboard.item)
  editor.customUiList.splice(insertAt, 0, item)
  if (clipboard.mode === 'cut') uiClipboard.value = null
  editor.notifyCustomUiChanged()
  editor.selectCustomUiRoot(insertAt)
  editor.commitCustomUiHistory(before, '粘贴高级UI')
}

function deleteControl(uiIndex: number, itemIndex: number): void {
  const ui = editor.customUiList[uiIndex]
  if (!ui || !window.confirm('确定删除该控件？此操作不可撤销。')) return
  const before = editor.cloneCustomUiListSnapshot()
  ui.ControlList.splice(itemIndex, 1)
  editor.notifyCustomUiChanged()
  editor.selectCustomUiRoot(uiIndex)
  editor.commitCustomUiHistory(before, '删除高级UI控件')
}

function moveControl(uiIndex: number, fromIndex: number, toIndex: number): void {
  const ui = editor.customUiList[uiIndex]
  if (!ui) return
  const count = ui.ControlList.length
  if (fromIndex < 0 || fromIndex >= count || toIndex < 0 || toIndex > count) return
  const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex
  if (insertIndex === fromIndex) return
  const before = editor.cloneCustomUiListSnapshot()
  const [item] = ui.ControlList.splice(fromIndex, 1)
  if (!item) return
  ui.ControlList.splice(insertIndex, 0, item)
  editor.notifyCustomUiChanged()
  editor.selectCustomUiControl(uiIndex, insertIndex)
  editor.commitCustomUiHistory(before, '移动高级UI控件')
}

function cloneControlItem(source: CustomUIItem): CustomUIItem {
  return hydrateIntoInstance(
    new CustomUIItem(),
    JSON.parse(JSON.stringify(source)),
    ['customUI', 'CUIList', 'ControlList'],
  )
}

function copyControl(uiIndex: number, itemIndex: number): void {
  const item = editor.customUiList[uiIndex]?.ControlList[itemIndex]
  if (!item) return
  controlClipboard.value = {
    mode: 'copy',
    item: cloneControlItem(item as CustomUIItem),
  }
}

function cutControl(uiIndex: number, itemIndex: number): void {
  const ui = editor.customUiList[uiIndex]
  if (!ui) return
  const item = ui.ControlList[itemIndex]
  if (!item) return
  const before = editor.cloneCustomUiListSnapshot()
  const [removed] = ui.ControlList.splice(itemIndex, 1)
  if (!removed) return
  controlClipboard.value = {
    mode: 'cut',
    item: removed as CustomUIItem,
  }
  editor.notifyCustomUiChanged()
  const nextIndex = Math.min(itemIndex, ui.ControlList.length - 1)
  if (nextIndex >= 0) editor.selectCustomUiControl(uiIndex, nextIndex)
  else editor.selectCustomUiRoot(uiIndex)
  editor.commitCustomUiHistory(before, '剪切高级UI控件')
}

function pasteControl(target: ContextTarget): void {
  const clipboard = controlClipboard.value
  const ui = editor.customUiList[target.uiIndex]
  if (!clipboard || !ui) return
  const before = editor.cloneCustomUiListSnapshot()
  const insertAt = target.itemIndex === null
    ? ui.ControlList.length
    : Math.min(target.itemIndex + 1, ui.ControlList.length)
  const item = (clipboard.mode === 'cut'
    ? clipboard.item
    : cloneControlItem(clipboard.item as CustomUIItem)) as CustomUIItem
  ui.ControlList.splice(insertAt, 0, item)
  if (clipboard.mode === 'cut') {
    controlClipboard.value = null
  }
  editor.notifyCustomUiChanged()
  editor.selectCustomUiControl(target.uiIndex, insertAt)
  editor.commitCustomUiHistory(before, '粘贴高级UI控件')
}

const ctxOptions = computed<DropdownOption[]>(() => {
  const target = ctxTarget.value
  if (!target) return [{ label: '新建高级UI', key: 'add-ui' }]
  if (target.itemIndex === null) {
    return [
      { label: '添加按钮', key: 'add-button' },
      { label: '添加文本', key: 'add-string' },
      { label: '添加数值', key: 'add-var' },
      { label: '添加图片', key: 'add-pic' },
      { label: '添加数值条', key: 'add-drap' },
      { label: '粘贴', key: 'paste-control', disabled: !hasControlClipboard.value },
      { label: '复制高级UI', key: 'copy-ui' },
      { label: '剪切高级UI', key: 'cut-ui', disabled: uiList.value.length <= 1 },
      { label: '粘贴高级UI', key: 'paste-ui', disabled: !hasUiClipboard.value },
      { label: '重命名', key: 'rename' },
      { label: '删除高级UI', key: 'delete-ui', disabled: uiList.value.length <= 1 },
    ]
  }
  return [
    { label: '复制', key: 'copy-control' },
    { label: '剪切', key: 'cut-control' },
    { label: '粘贴', key: 'paste-control', disabled: !hasControlClipboard.value },
    { label: '重命名', key: 'rename' },
    { label: '删除', key: 'delete-control' },
  ]
})

function openContextAt(x: number, y: number, target: ContextTarget | null): void {
  ctxTarget.value = target
  ctxX.value = x
  ctxY.value = y
  ctxShow.value = true
}

function onBlankContextMenu(event: MouseEvent): void {
  const target = event.target
  if (target instanceof Element && target.closest('.n-tree-node')) return
  event.preventDefault()
  if (isTouchGeneratedContextMenu(event)) return
  openContextAt(event.clientX, event.clientY, null)
}

function openRowContext(event: MouseEvent, target: ContextTarget | null): void {
  event.preventDefault()
  event.stopPropagation()
  if (isTouchGeneratedContextMenu(event)) return
  openContextAt(event.clientX, event.clientY, target)
}

function isTouchGeneratedContextMenu(event: MouseEvent): boolean {
  const sourceCapabilities = (event as MouseEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } }).sourceCapabilities
  return Boolean(sourceCapabilities?.firesTouchEvents) || performance.now() - lastTouchContextAt < 900
}

function rememberTouchContext(event: PointerEvent): void {
  if (event.pointerType !== 'mouse') {
    lastTouchContextAt = performance.now()
  }
}

function openMoreMenu(event: MouseEvent, target: ContextTarget): void {
  event.preventDefault()
  event.stopPropagation()
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  openContextAt(rect.right, rect.bottom, target)
}

function renderUiTreeLabel({ option }: { option: TreeOption }) {
  const node = getUiTreeNode(option)
  const canShowMore =
    (node.kind === 'root' || node.kind === 'control')
    && selectedUiKeys.value.includes(node.key)
  return h('span', { class: 'custom-ui-node-label' }, [
    h('span', { class: 'custom-ui-node-label__text' }, String(option.label ?? '')),
    canShowMore
      ? h(
        'span',
        {
          class: 'custom-ui-node-more',
          role: 'button',
          tabindex: 0,
          title: '更多操作',
          'aria-label': '更多操作',
          onClick: (event: MouseEvent) => openMoreMenu(event, node.target),
          onPointerdown: (event: PointerEvent) => event.stopPropagation(),
        },
        '更多',
      )
      : null,
  ])
}

function openRename(parsed: ContextTarget): void {
  const ui = editor.customUiList[parsed.uiIndex]
  if (!ui) return
  renameTarget.value = parsed
  if (parsed.itemIndex === null) {
    renameDefaultName.value = ui.Name?.trim() || 'UI界面'
  } else {
    const item = ui.ControlList[parsed.itemIndex]
    renameDefaultName.value = item ? (item.Type === CustomUIItem.controlType.Drap ? item.Color : item.ImagePath2).trim() : ''
  }
  renameModalOpen.value = true
}

function confirmRename(value: string): boolean {
  const target = renameTarget.value
  const nextName = value.trim()
  if (!target) return false
  if (!nextName) {
    message.warning('请输入名称')
    return false
  }
  const before = editor.cloneCustomUiListSnapshot()
  if (target.itemIndex === null) {
    editor.customUiList[target.uiIndex]!.Name = nextName
  } else {
    const item = editor.customUiList[target.uiIndex]?.ControlList[target.itemIndex]
    if (!item) return false
    if (item.Type === CustomUIItem.controlType.Drap) item.Color = nextName
    else item.ImagePath2 = nextName
  }
  editor.notifyCustomUiChanged()
  editor.commitCustomUiHistory(before, '重命名高级UI')
  renameTarget.value = null
  return true
}

function onContextSelect(key: string | number): void {
  ctxShow.value = false
  const target = ctxTarget.value
  if (key === 'add-ui') addCustomUi()
  else if (key === 'add-button' && target) addControl(target.uiIndex, CustomUIItem.controlType.Button)
  else if (key === 'add-string' && target) addControl(target.uiIndex, CustomUIItem.controlType.String)
  else if (key === 'add-var' && target) addControl(target.uiIndex, CustomUIItem.controlType.Var)
  else if (key === 'add-pic' && target) addControl(target.uiIndex, CustomUIItem.controlType.Pic)
  else if (key === 'add-drap' && target) addControl(target.uiIndex, CustomUIItem.controlType.Drap)
  else if (key === 'copy-control' && target?.itemIndex !== null && target) copyControl(target.uiIndex, target.itemIndex)
  else if (key === 'cut-control' && target?.itemIndex !== null && target) cutControl(target.uiIndex, target.itemIndex)
  else if (key === 'paste-control' && target) pasteControl(target)
  else if (key === 'copy-ui' && target) copyCustomUi(target.uiIndex)
  else if (key === 'cut-ui' && target) cutCustomUi(target.uiIndex)
  else if (key === 'paste-ui' && target) pasteCustomUi(target.uiIndex)
  else if (key === 'rename' && target) openRename(target)
  else if (key === 'delete-ui' && target) deleteCustomUi(target.uiIndex)
  else if (key === 'delete-control' && target?.itemIndex !== null && target) deleteControl(target.uiIndex, target.itemIndex)
}

function getUiTreeNode(option: TreeOption): UiTreeNode {
  return option as UiTreeNode
}

function handleUiTreeSelect(keys: Array<string | number>): void {
  const key = keys[0]
  if (typeof key !== 'string') return
  const node = uiTreeNodeMap.value.get(key)
  if (!node) return
  const { uiIndex, itemIndex } = node.target
  if (node.kind === 'root') editor.selectCustomUiRoot(uiIndex)
  else if (node.kind === 'load') editor.selectCustomUiLoadEvent(uiIndex)
  else if (node.kind === 'after') editor.selectCustomUiAfterEvent(uiIndex)
  else if (itemIndex !== null) editor.selectCustomUiControl(uiIndex, itemIndex)
}

function uiTreeNodeProps({ option }: { option: TreeOption }) {
  const node = getUiTreeNode(option)
  if (node.kind === 'load' || node.kind === 'after') {
    return {}
  }
  return {
    onContextmenu: (event: MouseEvent) => openRowContext(event, node.target),
    onPointerdown: (event: PointerEvent) => rememberTouchContext(event),
  }
}

function allowUiDrop({
  dropPosition,
  node,
}: {
  dropPosition: 'before' | 'inside' | 'after'
  node: TreeOption
  phase: 'drag' | 'drop'
}): boolean {
  const drag = dragControl.value
  const target = getUiTreeNode(node)
  return Boolean(
    drag
    && drag.itemIndex !== null
    && target.kind === 'control'
    && target.target.itemIndex !== null
    && target.target.uiIndex === drag.uiIndex
    && dropPosition !== 'inside',
  )
}

function onUiDragStart(info: { node: TreeOption }): void {
  const node = getUiTreeNode(info.node)
  dragControl.value = node.kind === 'control' ? node.target : null
}

function onUiDragEnd(): void {
  dragControl.value = null
}

function onUiDrop(info: {
  node: TreeOption
  dragNode: TreeOption
  dropPosition: 'before' | 'inside' | 'after'
}): void {
  const target = getUiTreeNode(info.node)
  const drag = dragControl.value
  dragControl.value = null
  if (!drag || drag.itemIndex === null || target.kind !== 'control' || target.target.itemIndex === null) return
  const toIndex = info.dropPosition === 'after'
    ? target.target.itemIndex + 1
    : target.target.itemIndex
  moveControl(target.target.uiIndex, drag.itemIndex, toIndex)
}
</script>

<template>
  <div
    class="custom-ui-list-wrap"
    @contextmenu="onBlankContextMenu"
    @pointerdown="rememberTouchContext"
  >
    <button type="button" class="custom-ui-add-button" @click="addCustomUi">新建高级UI</button>
    <AppTreeView
      :allow-drop="allowUiDrop"
      :data="uiTreeData"
      :draggable="true"
      :expanded-keys="uiExpandedKeys"
      :node-props="uiTreeNodeProps"
      :render-label="renderUiTreeLabel"
      :selected-keys="selectedUiKeys"
      @update:selected-keys="handleUiTreeSelect"
      @dragstart="onUiDragStart"
      @dragend="onUiDragEnd"
      @drop="onUiDrop"
    />
    <div v-if="!uiList.length" class="custom-ui-empty">右键空白处新建高级UI</div>
    <n-dropdown
      trigger="manual"
      placement="bottom-start"
      :show="ctxShow"
      :x="ctxX"
      :y="ctxY"
      :options="ctxOptions"
      @select="onContextSelect"
      @clickoutside="ctxShow = false"
    />
    <TextInputDialog
      v-model:show="renameModalOpen"
      title="重命名"
      :model-value="renameDefaultName"
      placeholder="请输入名称"
      :confirm="confirmRename"
    />
  </div>
</template>

<style scoped>
.custom-ui-list-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--theme-bg-panel);
  -webkit-touch-callout: none;
}

.custom-ui-add-button {
  border: 0;
  color: var(--theme-text-secondary);
  background: var(--theme-bg-secondary);
  font-size: var(--app-font-size-xs);
  cursor: pointer;
  appearance: none;
}

.custom-ui-add-button {
  flex: 0 0 auto;
  margin: var(--app-space-sm) var(--app-space-sm) var(--app-space-xs);
  padding: var(--app-space-xs) var(--app-space-sm);
}

.custom-ui-list-wrap :deep(.custom-ui-node-label) {
  display: inline-flex;
  align-items: center;
  gap: var(--app-space-xs);
  min-width: 0;
}

.custom-ui-list-wrap :deep(.custom-ui-node-label__text) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-ui-list-wrap :deep(.custom-ui-node-more) {
  all: unset;
  color: var(--theme-text-very-muted);
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
}

.custom-ui-list-wrap :deep(.custom-ui-node-more:hover),
.custom-ui-list-wrap :deep(.custom-ui-node-more:focus-visible) {
  color: var(--theme-text-secondary);
}

@media (hover: hover) and (pointer: fine) {
  .custom-ui-list-wrap :deep(.custom-ui-node-more:focus-visible) {
    display: inline-block;
  }
}

.custom-ui-empty {
  padding: 16px 12px;
  color: var(--theme-text-muted);
  font-size: var(--app-font-size-sm);
  line-height: 1.5;
}
</style>
